/*
  CURBi header — mobile navigation drawer, and the flavour mega menu.

  Dawn's menu-drawer is built around its own <details>/<summary> markup and
  multi-level menus, so forking the header meant owning this. trapFocus,
  removeTrapFocus and the overflow-hidden body class come from Dawn's global.js,
  which is what its own drawers use — no second focus-trap implementation.

  The drawer carries the `hidden` attribute at rest so it stays closed without
  JS. Opening removes `hidden` first and adds `.is-open` on the next frame,
  because a hidden element has no box to transition from.
*/
class CurbiHeader extends HTMLElement {
  connectedCallback() {
    this.burger = this.querySelector('.curbi-header__burger');
    this.drawer = this.querySelector('.curbi-nav-drawer');
    this.panel = this.querySelector('.curbi-nav-drawer__panel');

    this.onKeydown = this.onKeydown.bind(this);
    this.addEventListener('keydown', this.onKeydown);

    this.connectMega();

    if (!this.burger || !this.drawer) return;

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);

    this.burger.addEventListener('click', this.open);
    this.drawer.querySelectorAll('[data-curbi-nav-close]').forEach((el) => {
      el.addEventListener('click', this.close);
    });

    // A link tap should close the drawer even when the browser serves the next
    // page from bfcache and never re-runs connectedCallback.
    window.addEventListener('pageshow', this.close);
  }

  /*
    Flavour mega menu.

    Pointer only, and on purpose — see the long note on the trigger in
    sections/curbi-header.liquid. There is no focus handler here and no
    aria-expanded to maintain: the trigger is an ordinary link to the flavours
    collection, and this panel is a shortcut laid over it.

    Opening is bound to the trigger and to the panel; closing to the other
    interactive items in the bar and to the pointer leaving the header. What is
    deliberately NOT bound is the bar's own empty space: the pointer has to
    cross ~34px of it travelling from the link down into the panel, and closing
    on that would make the panel unreachable by the gesture that opens it.
  */
  connectMega() {
    this.megaTrigger = this.querySelector('[data-curbi-mega-trigger]');
    this.mega = this.querySelector('[data-curbi-mega]');

    if (!this.megaTrigger || !this.mega) return;

    // A touch tap fires pointerenter immediately before the link navigates, so
    // without this a phone in landscape past 990px would flash the panel open
    // on the way to the collection page. Hover-capable pointers only.
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    this.openMega = this.openMega.bind(this);
    this.closeMega = this.closeMega.bind(this);

    this.megaTrigger.addEventListener('pointerenter', this.openMega);
    this.mega.addEventListener('pointerenter', this.openMega);
    this.addEventListener('pointerleave', this.closeMega);

    // Everything else in the bar that can be pointed at closes it, which is
    // what makes the panel feel like it belongs to Shop rather than to the
    // header as a whole.
    this.querySelectorAll('.curbi-header__nav-link, .curbi-header__cart, .curbi-header__cta').forEach((el) => {
      if (el !== this.megaTrigger) el.addEventListener('pointerenter', this.closeMega);
    });

    // A pointer user who starts tabbing should not be left with a panel open
    // behind their focus ring. Focus landing inside the panel is fine.
    this.addEventListener('focusin', (event) => {
      if (!this.mega.contains(event.target)) this.closeMega();
    });

    window.addEventListener('pageshow', this.closeMega);
  }

  openMega() {
    this.classList.add('is-mega-open');
  }

  closeMega() {
    this.classList.remove('is-mega-open');
  }

  open() {
    this.drawer.removeAttribute('hidden');
    document.body.classList.add('overflow-hidden');
    this.burger.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      this.drawer.classList.add('is-open');
      const firstLink = this.drawer.querySelector('.curbi-nav-drawer__link');
      trapFocus(this.panel, firstLink || this.panel);
    });
  }

  close() {
    if (this.drawer.hasAttribute('hidden')) return;

    this.drawer.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    this.burger.setAttribute('aria-expanded', 'false');
    removeTrapFocus(this.burger);

    const finish = () => this.drawer.setAttribute('hidden', '');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
    } else {
      this.panel.addEventListener('transitionend', finish, { once: true });
    }
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;

    this.closeMega();
    if (this.drawer && this.burger) this.close();
  }
}

customElements.define('curbi-header', CurbiHeader);
